'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Checkbox, Input } from '@nwwp/ui-shadcn'
import { useVeritonic } from '@nwwp/veritonic'

import { createLeadMagnetLead } from '~/actions/create-lead-magnet-lead'
import { ButtonCTA } from '~/components/ButtonCTA'
import { NumberInput } from '~/components/NumberInput'
import { RangeSelectInput } from '~/components/RangeSelectInput'
import { buildLeadMagnetSchema } from '~/lib/schemas/lead-magnet-schema'
import {
  INVESTABLE_ASSETS_OPTIONS,
  isLeadMagnetQualified,
  saveLeadMagnetQualification,
} from '~/utils/lead-magnet-qualification'
import { DEFAULT_FORM_FIELDS, LeadMagnetPage } from '~/utils/lead-magnets'
import { saveSchedulePrefill } from '~/utils/schedule-prefill'
import { getTrackingParams } from '~/utils/tracking'
import { PhoneDisclosure } from './PhoneDisclosure'

type Props = {
  magnet: LeadMagnetPage
  /** Called after a successful reveal submit (cookie already set). */
  onSuccess?: () => void
  /** Fire once on first form field interaction (Veritonic flow start). */
  onFlowStart?: () => void
}

export function LeadCaptureForm({ magnet, onSuccess, onFlowStart }: Props) {
  const router = useRouter()
  const { trackFlowComplete } = useVeritonic()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const flowStartedRef = useRef(false)
  const fields = magnet.formFields ?? DEFAULT_FORM_FIELDS
  const schema = useMemo(() => buildLeadMagnetSchema(fields), [fields])

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      age: undefined,
      household_income: '',
      schedule_call_interest: fields.includes('schedule_call_interest') ? true : undefined,
      investable_assets_range: undefined,
    },
  })

  const phone = useWatch({ control, name: 'phone' })

  const markFlowStarted = () => {
    if (flowStartedRef.current || !onFlowStart) return
    flowStartedRef.current = true
    onFlowStart()
  }

  const onSubmit = async (data: Parameters<typeof createLeadMagnetLead>[0]) => {
    try {
      setIsSubmitting(true)
      setSubmitError(null)
      const success = await createLeadMagnetLead(data, { ...getTrackingParams(), lead_magnet_slug: magnet.slug })
      if (!success) {
        setSubmitError('Something went wrong. Please try again.')
        return
      }
      try {
        trackFlowComplete()
      } catch (e) {
        console.warn('trackFlowComplete failed', { error: e })
      }
      saveSchedulePrefill({
        first_name: data.first_name ?? '',
        last_name: data.last_name ?? '',
        email: data.email,
        phone: data.phone,
      })
      saveLeadMagnetQualification(
        isLeadMagnetQualified(data.household_income || undefined, data.investable_assets_range)
      )
      if (magnet.ctaType === 'redirect' || magnet.ctaType === 'reveal') {
        document.cookie = `${magnet.slug}_unlocked=1; path=/; SameSite=Lax`
        if (magnet.ctaType === 'reveal') onSuccess?.()
        else router.push(magnet.ctaUrl)
      } else if (magnet.ctaType === 'schedule') {
        router.push(`/lp/${magnet.slug}/get-started`)
      } else if (magnet.ctaType === 'email') setSubmitSuccess(true)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return <p className="text-center text-body">Thanks! We&apos;ll be in touch shortly.</p>
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} onFocusCapture={markFlowStarted} className="flex w-full flex-col gap-6">
      {fields.includes('household_income') && (
        <Controller
          name="household_income"
          control={control}
          render={({ field }) => (
            <NumberInput
              name={field.name}
              ref={field.ref}
              value={(field.value as string | number | undefined) ?? ''}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(e.target.value)}
              placeholder="Household Income *"
              prefix="$"
              error={errors.household_income?.message}
            />
          )}
        />
      )}

      {fields.includes('investable_assets_range') && (
        <Controller
          name="investable_assets_range"
          control={control}
          render={({ field }) => (
            <RangeSelectInput
              id="investable_assets_range"
              label="Investable Assets *"
              name={field.name}
              value={field.value ?? ''}
              onChange={(value) => field.onChange(value)}
              options={INVESTABLE_ASSETS_OPTIONS}
              error={errors.investable_assets_range?.message}
              onBlur={field.onBlur}
            />
          )}
        />
      )}

      {fields.includes('age') && (
        <Input
          {...register('age', { setValueAs: (v) => (v === '' ? undefined : v) })}
          type="number"
          placeholder="Age *"
          error={errors.age?.message ?? null}
        />
      )}

      {fields.includes('first_name') && (
        <Input {...register('first_name')} placeholder="First Name *" error={errors.first_name?.message ?? null} />
      )}

      {fields.includes('last_name') && (
        <Input {...register('last_name')} placeholder="Last Name *" error={errors.last_name?.message ?? null} />
      )}

      {fields.includes('email') && (
        <Input {...register('email')} type="email" placeholder="Email *" error={errors.email?.message ?? null} />
      )}

      {fields.includes('schedule_call_interest') && (
        <Controller
          name="schedule_call_interest"
          control={control}
          render={({ field }) => (
            <div className="flex items-start gap-2">
              <Checkbox
                id="schedule_call_interest"
                checked={!!field.value}
                onCheckedChange={field.onChange}
                className="mt-0.5"
              />
              <label htmlFor="schedule_call_interest" className="cursor-pointer text-body">
                I am interested in scheduling a call with a financial advisor
              </label>
            </div>
          )}
        />
      )}

      {fields.includes('phone') && (
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <NumberInput
              name={field.name}
              ref={field.ref}
              value={(field.value as string | number | undefined) ?? ''}
              onBlur={field.onBlur}
              onChange={(e) => field.onChange(e.target.value)}
              mask="phone"
              placeholder="Phone"
              error={errors.phone?.message}
            />
          )}
        />
      )}

      {fields.includes('phone') && !!phone && (
        <PhoneDisclosure ctaText={magnet.ctaText}>{magnet.phoneDisclosure}</PhoneDisclosure>
      )}

      <ButtonCTA type="submit" disabled={!isValid || isSubmitting}>
        {isSubmitting && (
          <span
            className="size-4 animate-spin rounded-full border-2 border-black/30 border-t-black"
            aria-hidden="true"
          />
        )}
        {isSubmitting ? 'Submitting...' : magnet.ctaText}
      </ButtonCTA>

      {submitError && <p className="text-body-sm text-red">{submitError}</p>}
    </form>
  )
}
